import { sleepStages, SleepStageKey } from 'constants/constants';
import { generateUUID } from 'utils/Helpers/UUID';

export const buildMeasurement = (
  className: string,
  date: string,
  deleted: number,
  uid: string,
  unit: string,
  value: any,
  via: string,
  source: string,
  testId?: string
) => {
  const measurement = {
    class: className,
    date,
    deleted,
    id: generateUUID(),
    modificationTime: new Date().getTime(),
    via,
    source,
    unit,
    value,
    uid,
    testId
  };
  return measurement;
};

/**
 *
 * Function to check if a patient has measurements in a specific category
 *
 * @param allClasses all measurements the patient has
 * @param specificClasses the specific category we want to check
 * @returns if the measurements include one of the ones in the category
 */
export const includesClass = (allClasses: string[], specificClasses: string[]) => {
  let found = false;
  specificClasses.forEach((item) => {
    if (allClasses.includes(item)) {
      found = true;
      return found;
    }
  });
  return found;
};

export const convertGlucoseUnit = (
  value: number,
  sourceUnit: IGlucoseUnit,
  targetUnit: IGlucoseUnit
): number => {
  const mmolToG = 0.180182;
  const mmolToMg = 18.0182;
  let convertedUnit: number = value;
  switch (sourceUnit) {
    case 'g/L':
      if (targetUnit === 'mg/dL') {
        convertedUnit = value * 100;
      } else if (targetUnit === 'mmol/L') {
        convertedUnit = value / mmolToG;
      }
      break;
    case 'mg/dL':
      if (targetUnit === 'g/L') {
        convertedUnit = value / 100;
      } else if (targetUnit === 'mmol/L') {
        convertedUnit = value / mmolToMg;
      }
      break;
    case 'mmol/L':
      if (targetUnit === 'g/L') {
        convertedUnit = value * mmolToG;
      } else if (targetUnit === 'mg/dL') {
        convertedUnit = value * mmolToMg;
      }
      break;
  }
  return convertedUnit;
};

/**
 * Sorting order weight
 * The higher the number, the more to the right it is
 */
export const getClassWeight = (className: string) => {
  switch (className) {
    case 'IOPUnspecified':
      return 3;
    case 'IOPLeft':
      return 2;
    case 'IOPRight':
      return 1;
    default:
      return 0;
  }
};

export const getMeasurementsOptions = (measurementClasses: IMeasurementClass[]) => {
  return measurementClasses.reduce<{
    [key: string]: IMeasurementOptions[];
  }>((acc, measClass) => {
    if (measClass.options) {
      acc[measClass.className] = measClass.options;
    }
    return acc;
  }, {});
};

export const getMeasurementLimits = (
  measurementClasses: IMeasurementClass[],
  className: string,
  unit: string
) => {
  return measurementClasses.find((x) => {
    return (
      x.className === className &&
      (unit
        ? x.units?.some(
            (unitDetail: { [key: string]: IMeasurementUnitDetails }) => unitDetail[unit]
          )
        : true)
    );
  });
};

export const getMeasurementsUnits = (measurementClasses: IMeasurementClass[]) => {
  return measurementClasses.reduce(
    (acc, m) => {
      const units = m.units?.map((unitDetail) => Object.keys(unitDetail)[0]) || [];
      acc[m.className] = units;
      return acc;
    },
    {} as { [key: string]: string[] }
  );
};

export const extractViaInfo = (vias: string[], key: string) => {
  const event_string = vias.find((split) => split.substr(0, split.indexOf('=')) === key);
  return event_string?.substr(event_string?.indexOf('=') + 1, event_string?.length - 1);
};

export const extractSleepStages = (via: string): Partial<Record<SleepStageKey, number>> => {
  if (!via) return {};
  const via_split = via.split(';');
  const stages: Partial<Record<SleepStageKey, number>> = {};
  const raw = extractViaInfo(via_split, 'sleep');
  if (!raw) return stages;
  const values = raw.split(',');
  sleepStages.forEach((key, index) => {
    const parsed = parseFloat(values[index]);
    stages[key] = Number.isNaN(parsed) ? 0 : parsed;
  });
  return stages;
};

export const getSleepRecordValue = (record: { via?: string; value?: any; display_value?: any }) => {
  const stages = extractSleepStages(record.via ?? '');
  const presentKeys = sleepStages.filter((key) => stages[key] != null);
  if (presentKeys.length) {
    return presentKeys.reduce((sum, key) => sum + (stages[key] ?? 0), 0);
  }
  return parseFloat(record.value ?? record.display_value);
};

export enum BPEvents {
  None = 0,
  FirstMeasurement = 1,
  SecondMeasurement = 2,
  ThirdMeasurement = 3,
  Average = 4
}

export enum MeasurementError {
  Device = 'device',
  Body = 'body',
  Cuff = 'cuff'
}

const measurementErrorFlags: Record<MeasurementError, keyof IMeasurementRecord> = {
  [MeasurementError.Device]: 'measurementError',
  [MeasurementError.Body]: 'bodyMovementError',
  [MeasurementError.Cuff]: 'cuffFitError'
};

export const measurementErrorLabelKeys: Record<MeasurementError, string> = {
  [MeasurementError.Device]: 'deviceErrorKey',
  [MeasurementError.Body]: 'bodyErrorKey',
  [MeasurementError.Cuff]: 'cuffErrorKey'
};

export const measurementErrorTooltipKeys: Record<MeasurementError, string> = {
  [MeasurementError.Device]: 'deviceErrorTooltipKey',
  [MeasurementError.Body]: 'bodyErrorTooltipKey',
  [MeasurementError.Cuff]: 'cuffErrorTooltipKey'
};

// The API omits a flag entirely when it is unknown, so only an explicit true is an error.
export const getMeasurementErrors = (record: IMeasurementRecord): MeasurementError[] =>
  Object.values(MeasurementError).filter((error) => record[measurementErrorFlags[error]] === true);
